// components/looseStone/LooseStoneItemForm.jsx
import React, { useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import { Controller, useWatch } from "react-hook-form";
import { motion } from "framer-motion";
import { FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { handleDropDowns } from "../../config/dropDownHelpers";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (b) => ({ ...b, height: "30px" }),
  dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
  clearIndicator: (b) => ({ ...b, padding: "4px" }),
  valueContainer: (b) => ({ ...b, padding: "0 6px", height: "30px" }),
  input: (b) => ({ ...b, margin: 0, padding: 0 }),
  menu: (b) => ({ ...b, fontSize: "12px", zIndex: 50 }),
  menuList: (b) => ({ ...b, maxHeight: "140px", overflowY: "auto" }),
  singleValue: (b) => ({ ...b, fontSize: "12px" }),
};

const LooseStoneItemForm = ({
  control,
  register,
  errors,
  rowindex,
  fieldId,
  firstFieldRef,
  registerFirstField,
  looseStoneFields,
  onDelete,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",
  setValue,
}) => {
  const stonesData = useSelector((state) => state.stones?.stones);
  const stoneAttr = useSelector((state) => state.stoneAttr?.stoneAttr);

  const pickOptions = (t) =>
    (stoneAttr || [])
      .filter((x) => x?.type === t && x?.isActive !== false)
      .map((x) => ({ ...x }));

  const shapes = useMemo(() => pickOptions("shape"), [stoneAttr]);
  const colors = useMemo(() => pickOptions("color"), [stoneAttr]);
  const sizes = useMemo(() => pickOptions("size"), [stoneAttr]);
  const settingTypes = useMemo(() => pickOptions("settingType"), [stoneAttr]);

  const formRef = useRef(null);

  const stnWt = useWatch({ control, name: `looseStones.${rowindex}.stnWt` });
  const stnPcs = useWatch({ control, name: `looseStones.${rowindex}.stnPcs` });
  const isWtDisabled = Number(stnPcs) > 0;
  const isPcsDisabled = Number(stnWt) > 0;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Delete") {
        if (formRef.current && formRef.current.contains(document.activeElement)) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete]);

  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: rowindex * 0.04 }}
      id={`loose-card-${fieldId}`}
      data-field-id={fieldId}
      className={`stock-card ${cardHeightClass} snap-start p-2 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg mb-0 bg-white relative overflow-y-auto`}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between sticky top-0 bg-white pt-1 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">#{rowindex + 1} • Loose Stone</h3>
        <div className="flex-1 h-px bg-gray-200 mx-2" />
        <button
          type="button"
          onClick={onDelete}
          className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
          title="Delete this row (Ctrl + Delete)"
        >
          <FaTrash className="text-[14px]" />
        </button>
      </div>

      {/* Fields grid */}
      <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
        {looseStoneFields?.map((cfg, idx) => {
          const path = `looseStones.${rowindex}.${cfg.name}`;
          const stoneNamePath = `looseStones.${rowindex}.stoneName`;
          const rules = {
            required: cfg?.mandatory ? `${cfg.label} is required` : false,
          };

          const colSpan = ["stnDescription"].includes(cfg.name)
            ? "md:col-span-3"
            : ["stoneName", "shapeName", "colorName", "sizeName", "settingTypeName"].includes(
                cfg.name
              )
            ? "md:col-span-2"
            : "md:col-span-1";

          return (
            <div key={cfg.name} className={colSpan}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {cfg.label} {cfg?.mandatory && <span className="text-red-500">*</span>}
              </label>

              {/* DROPDOWN */}
              {cfg.type === "dropdown" && (
                <>
                  <Controller
                    name={path}
                    control={control}
                    rules={rules}
                    render={({ field: { onChange, ...field } }) => (
                      <Select
                        {...field}
                        ref={(ref) => {
                          field.ref(ref);
                          if (idx === 0 && registerFirstField) {
                            registerFirstField(fieldId, { focus: () => ref?.focus?.() });
                          }
                          if (idx === 0 && firstFieldRef) firstFieldRef.current = ref;
                        }}
                        options={handleDropDowns({
                          key: cfg["name"],
                          stoneName: stoneNamePath,
                          stonesData,
                          shapes,
                          colors,
                          sizes,
                          settingTypes,
                        })}
                        classNamePrefix="react-select"
                        menuPlacement="bottom"
                        isClearable
                        isSearchable
                        // readonly for derived attrs (auto from stone selection)
                        isDisabled={
                          cfg.name === "shapeName" ||
                          cfg.name === "colorName" ||
                          cfg.name === "sizeName" ||
                          cfg.name === "settingTypeName"
                        }
                        onChange={(opt) => {
                          onChange(opt ?? null);

                          if (cfg.name === "stoneName") {
                            if (!opt) {
                              setValue(`looseStones.${rowindex}.stoneName`, null);
                              setValue(`looseStones.${rowindex}.shapeName`, null);
                              setValue(`looseStones.${rowindex}.colorName`, null);
                              setValue(`looseStones.${rowindex}.sizeName`, null);
                              setValue(`looseStones.${rowindex}.settingTypeName`, null);
                              setValue(`looseStones.${rowindex}.stnUnit`, {
                                value: "gram",
                                label: "gram",
                              });
                              setValue(`looseStones.${rowindex}.stnWt`, "");
                              setValue(`looseStones.${rowindex}.stnPcs`, "");
                              setValue(`looseStones.${rowindex}.stnDescription`, "");
                              return;
                            }

                            setValue(`looseStones.${rowindex}.stoneName`, {
                              value: opt?.value,
                              label: opt?.label,
                            });
                            setValue(
                              `looseStones.${rowindex}.shapeName`,
                              opt?.info?.shape?._id
                                ? { value: opt.info.shape._id, label: opt.info.shape.name }
                                : null
                            );
                            setValue(
                              `looseStones.${rowindex}.colorName`,
                              opt?.info?.color?._id
                                ? { value: opt.info.color._id, label: opt.info.color.name }
                                : null
                            );
                            setValue(
                              `looseStones.${rowindex}.sizeName`,
                              opt?.info?.size?._id
                                ? { value: opt.info.size._id, label: opt.info.size.name }
                                : null
                            );
                            setValue(
                              `looseStones.${rowindex}.settingTypeName`,
                              opt?.info?.settingType?._id
                                ? {
                                    value: opt.info.settingType._id,
                                    label: opt.info.settingType.name,
                                  }
                                : null
                            );
                            setValue(
                              `looseStones.${rowindex}.stnUnit`,
                              opt?.info?.unit
                                ? { value: opt.info.unit, label: opt.info.unit }
                                : { value: "gram", label: "gram" }
                            );
                            setValue(
                              `looseStones.${rowindex}.stnWt`,
                              opt?.info?.stnPopupWt ? opt.info.stnPopupWt : ""
                            );
                            setValue(
                              `looseStones.${rowindex}.stnPcs`,
                              opt?.info?.stnPopupPcs ? opt.info.stnPopupPcs : ""
                            );
                            setValue(
                              `looseStones.${rowindex}.stnDescription`,
                              opt?.info?.stnPopupDescription ? opt.info.stnPopupDescription : ""
                            );
                          }
                        }}
                        styles={selectStyles}
                      />
                    )}
                  />
                  {errors?.looseStones?.[rowindex]?.[cfg.name] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.looseStones[rowindex][cfg.name].message}
                    </p>
                  )}
                </>
              )}

              {/* TEXT / NUMBER */}
              {["text", "number"].includes(cfg.type) && (
                <>
                  <input
                    type={cfg.type === "number" ? "number" : "text"}
                    {...register(path, {
                      ...rules,
                      onChange: (e) => {
                        const v = e?.target?.value ?? "";
                        if (cfg.name === "stnWt" && v && Number(v) > 0) {
                          setValue(`looseStones.${rowindex}.stnPcs`, "");
                        }
                        if (cfg.name === "stnPcs" && v && Number(v) > 0) {
                          setValue(`looseStones.${rowindex}.stnWt`, "");
                        }
                      },
                    })}
                    placeholder={cfg.placeholder || ""}
                    disabled={
                      (cfg.name === "stnWt" && isWtDisabled) ||
                      (cfg.name === "stnPcs" && isPcsDisabled)
                    }
                    className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400
                               focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                  />
                  {errors?.looseStones?.[rowindex]?.[cfg.name] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.looseStones[rowindex][cfg.name].message}
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default LooseStoneItemForm;
